import React from 'react';
import LegalDocumentPage from '../components/LegalDocumentPage';

/*
 * /terms — Terms of Service.
 *
 * Good-faith draft grounded in what the app actually does (social voting:
 * matchups, brackets/This-or-That, tier lists, pools, communities; vote,
 * comment, like, follow, mention, share; report/block/mute; 30-day account
 * deletion). Written in plain English and kept as an array of
 * { heading, body } strings so LegalDocumentPage renders it with no extra
 * wiring. Owner details filled 2026-07-16: entity "Cordell Jenkins II",
 * governing law the State of Kansas, contact cordelljenkins1914@gmail.com,
 * effective July 16, 2026.
 *
 * NOTE FOR MAINTAINERS (deliberately NOT user-visible): this was drafted
 * without counsel and should be reviewed by a qualified attorney before
 * real scale. That caveat belongs HERE, in the source — it used to be the
 * first sentence a reader saw on the live page, which read as unfinished
 * placeholder text and contradicted LegalDocumentPage's own 5.1.1 rule.
 */

const SECTIONS = [
  {
    heading: 'Introduction and acceptance',
    body:
      `Effective date: July 16, 2026. These Terms of Service (the ` +
      `"Terms") are an agreement between you and Cordell Jenkins II ("Matchup," ` +
      `"we," "us," or "our"), which operates the Matchup website and mobile apps ` +
      `(together, the "Service"). Matchup is a social voting community where ` +
      `people create matchups, brackets and This-or-That polls, tier lists, item ` +
      `pools, and communities, and where they vote, comment, like, follow, ` +
      `mention, and share. By creating an account, casting a vote, or otherwise ` +
      `using the Service, you agree to these Terms and to our Privacy Policy. If ` +
      `you do not agree, please do not use the Service.`,
  },
  {
    heading: 'Who can use Matchup',
    body:
      `You must be at least 13 years old to use Matchup. If you are under the age ` +
      `of majority where you live, you may use the Service only with the ` +
      `involvement of a parent or guardian. Some countries set a higher minimum ` +
      `age for using online services without parental consent: where local law ` +
      `requires it (for example, in parts of the European Union and the United ` +
      `Kingdom, where the threshold can be as high as 16), you must meet that ` +
      `higher age, or have verifiable parental consent, before using the Service. ` +
      `By using Matchup you confirm that you meet these requirements and that you ` +
      `are able to enter into a binding agreement with us.`,
  },
  {
    heading: 'Your account',
    body:
      `You are responsible for the information you provide when you register, for ` +
      `keeping your login credentials secure, and for all activity that happens ` +
      `under your account. Please give accurate information and keep it current. ` +
      `Do not share your password, and tell us promptly at cordelljenkins1914@gmail.com if you ` +
      `believe your account has been accessed without your permission. You can ` +
      `manage your profile, your private-profile setting, and your notification ` +
      `preferences in Settings, and you can delete your account at any time as ` +
      `described below. We are not responsible for losses caused by unauthorized ` +
      `use of your account that results from your failure to keep your ` +
      `credentials secure.`,
  },
  {
    heading: 'Acceptable use',
    body:
      `Matchup is meant to be a fun, respectful place to compare things and share ` +
      `opinions. You agree not to post, upload, or share content, and not to ` +
      `behave, in ways that: harass, bully, threaten, or intimidate anyone; ` +
      `promote hate or discrimination against people based on protected ` +
      `characteristics; depict, encourage, or incite violence or self-harm; ` +
      `contain sexually explicit material, or any content that sexualizes minors; ` +
      `are spam, scams, or deceptive; infringe anyone's intellectual property, ` +
      `privacy, or other rights; impersonate any person, brand, or organization, ` +
      `or misrepresent your affiliation with them; reveal private or personal ` +
      `information about others without their consent; or break any applicable ` +
      `law. You also agree not to interfere with, disrupt, reverse engineer, ` +
      `scrape, or place an unreasonable load on the Service, and not to bypass ` +
      `any security, rate limit, or access control. If you see content that ` +
      `breaks these rules, please use the in-app Report tools; you can also Block ` +
      `or Mute other users to control what you see.`,
  },
  {
    heading: 'Your content and the license you grant us',
    body:
      `You keep ownership of the matchups, brackets, tier lists, pools, ` +
      `communities, titles, text, comments, photos, and other content you create ` +
      `or upload ("Your Content"). To operate the Service, you grant Matchup a ` +
      `worldwide, non-exclusive, royalty-free license to host, store, reproduce, ` +
      `adapt (for example, to resize or reformat images), publish, publicly ` +
      `display, and distribute Your Content, solely to run, secure, improve, and ` +
      `promote the Service and as you direct through features like posting, ` +
      `sharing, and community controls. This license is revocable: it ends when ` +
      `you delete Your Content or your account, except that we may keep residual ` +
      `copies for a reasonable period in routine backups, and we may retain ` +
      `content we are legally required to keep or that we need to handle a safety ` +
      `report or dispute. You confirm that you have the rights needed to post ` +
      `Your Content and to grant this license, and that Your Content does not ` +
      `violate these Terms or the law. We do not claim ownership of Your Content, ` +
      `and we do not sell it.`,
  },
  {
    heading: 'Votes, results, and voting integrity',
    body:
      `Votes, agreement scores, leaderboards, and similar features are meant to ` +
      `reflect genuine human opinion. You agree not to manipulate results, ` +
      `including by using bots, scripts, or automation; creating multiple or fake ` +
      `accounts; buying, selling, or coordinating votes; or otherwise casting or ` +
      `inflating votes that are not your own honest choices. Signed-out visitors ` +
      `may cast a limited number of votes (currently three) before we ask them to ` +
      `create an account, and you agree not to try to evade this limit or other ` +
      `rate limits. We may discard votes, recalculate counts, or remove results ` +
      `we reasonably believe are fraudulent or manipulated.`,
  },
  {
    heading: 'Communities and owner controls',
    body:
      `Matchup lets people create and run communities and their own matchups, ` +
      `brackets, tier lists, and pools. If you create one, you may have owner or ` +
      `moderator controls, such as setting visibility or audience, curating ` +
      `items, and managing participation, and you agree to use those controls ` +
      `responsibly and in line with these Terms. Owners and moderators are ` +
      `responsible for the spaces they run, but Matchup remains able to review ` +
      `reported content and to take action across the Service. Being able to see ` +
      `a community or a piece of content does not give you the right to copy, ` +
      `redistribute, or misuse it outside the Service.`,
  },
  {
    heading: 'Moderation and enforcement',
    body:
      `We want Matchup to stay safe and welcoming. We may review content and ` +
      `activity, including reports submitted by users, and a real person reviews ` +
      `reports. When content or conduct violates these Terms or the law, or where ` +
      `we reasonably believe it is necessary to protect users or the Service, we ` +
      `may remove or limit content, hide or age-gate it, reduce its distribution, ` +
      `warn you, or suspend or terminate your access, with or without notice ` +
      `depending on the circumstances. We try to act proportionately, but we are ` +
      `not obligated to host any particular content and we may decline, remove, ` +
      `or restrict content at our discretion consistent with these Terms.`,
  },
  {
    heading: 'Copyright and intellectual-property complaints',
    body:
      `We respect intellectual-property rights and expect you to do the same. If ` +
      `you believe content on Matchup infringes your copyright or other rights, ` +
      `contact us at cordelljenkins1914@gmail.com with enough detail to identify the work and ` +
      `the allegedly infringing content, your contact information, and a ` +
      `statement of your good-faith belief and your authority to act. Where ` +
      `applicable law (such as the U.S. Digital Millennium Copyright Act) ` +
      `provides a notice-and-takedown process, we will follow it, including ` +
      `removing or disabling access to content we determine is infringing and, ` +
      `in appropriate cases, terminating the accounts of repeat infringers. If ` +
      `your content was removed and you believe that was a mistake, you may ` +
      `contact us to dispute it.`,
  },
  {
    heading: 'Third-party links and content',
    body:
      `The Service may contain links to, or content from, third parties, and Your ` +
      `Content and other users' content reflect their authors, not Matchup. We do ` +
      `not endorse and are not responsible for third-party sites, services, or ` +
      `content, and your use of them is at your own risk and subject to their own ` +
      `terms.`,
  },
  {
    heading: 'Disclaimers',
    body:
      `The Service is provided "as is" and "as available," without warranties of ` +
      `any kind, whether express or implied, including implied warranties of ` +
      `merchantability, fitness for a particular purpose, title, and ` +
      `non-infringement. We do not warrant that the Service will be ` +
      `uninterrupted, secure, error-free, or free of harmful components, or that ` +
      `any content (including votes, results, and user posts) is accurate, ` +
      `reliable, or complete. Some jurisdictions do not allow certain warranty ` +
      `exclusions, so some of these exclusions may not apply to you.`,
  },
  {
    heading: 'Limitation of liability',
    body:
      `To the fullest extent permitted by law, Matchup and Cordell Jenkins II, ` +
      `together with our officers, employees, and service providers, will not be ` +
      `liable for any indirect, incidental, special, consequential, exemplary, or ` +
      `punitive damages, or for any loss of data, goodwill, or profits, arising ` +
      `out of or relating to your use of, or inability to use, the Service. To ` +
      `the fullest extent permitted by law, our total liability for all claims ` +
      `relating to the Service will not exceed the greater of the amount you paid ` +
      `us to use the Service in the twelve months before the claim (which for ` +
      `most users is zero) or one hundred U.S. dollars. Some jurisdictions do not ` +
      `allow certain limitations, so parts of this section may not apply to you.`,
  },
  {
    heading: 'Indemnification',
    body:
      `You agree to indemnify and hold harmless Matchup and Cordell Jenkins II, ` +
      `together with our officers, employees, and service providers, from and ` +
      `against any claims, damages, losses, liabilities, and expenses (including ` +
      `reasonable legal fees) arising out of or related to Your Content, your use ` +
      `of the Service, your violation of these Terms, or your violation of any ` +
      `law or the rights of any third party. We may assume the exclusive defense ` +
      `of any matter subject to indemnification, in which case you agree to ` +
      `cooperate with us.`,
  },
  {
    heading: 'Termination and account deletion',
    body:
      `You may stop using Matchup at any time, and you may delete your account ` +
      `from Settings. When you delete your account, it is deactivated immediately ` +
      `and scheduled for permanent deletion after 30 days; during that window ` +
      `your posts and votes are anonymized to "[deleted]," and you can cancel the ` +
      `deletion by signing back in before the 30 days end. After the window ` +
      `closes, your account and associated personal data are permanently deleted, ` +
      `except for information we are required or permitted to keep by law or for ` +
      `legitimate purposes such as security, dispute resolution, or backups. We ` +
      `may suspend or terminate your access if you violate these Terms or if we ` +
      `reasonably need to for security, legal, or operational reasons; where ` +
      `practical and lawful, we will give notice. Sections that by their nature ` +
      `should survive termination — for example, the content-license residuals, ` +
      `disclaimers, limitation of liability, and indemnification — will survive.`,
  },
  {
    heading: 'Changes to these Terms',
    body:
      `We may update these Terms from time to time. When we make material ` +
      `changes, we will update the "Last updated" date and, where appropriate, ` +
      `give additional notice through the Service. Changes take effect when ` +
      `posted unless we say otherwise, and your continued use of the Service ` +
      `after they take effect means you accept the updated Terms. If you do not ` +
      `agree to the changes, please stop using the Service and delete your ` +
      `account.`,
  },
  {
    heading: 'Governing law',
    body:
      `These Terms are governed by the laws of the State of Kansas, United States, ` +
      `without regard to its conflict-of-laws rules, and you and Matchup agree to ` +
      `the exclusive jurisdiction of the courts located there for any dispute ` +
      `that is not otherwise required to be resolved elsewhere, except where ` +
      `applicable local law lets you bring a claim in your home jurisdiction. ` +
      `Nothing in these Terms limits any consumer-protection rights that cannot ` +
      `be waived under the law that applies to you.`,
  },
  {
    heading: 'Apple App Store terms',
    body:
      `If you download the Matchup app from the Apple App Store, the following ` +
      `also applies and prevails over any conflicting term above to the extent it ` +
      `concerns your use of the iOS app. These Terms are between you and ` +
      `Cordell Jenkins II only, and not with Apple, and Apple is not responsible for ` +
      `the app or its content. Apple has no obligation to provide any maintenance ` +
      `or support for the app. If the app fails to conform to any applicable ` +
      `warranty, you may notify Apple, and Apple may refund the purchase price ` +
      `(if any); to the maximum extent permitted by law, Apple has no other ` +
      `warranty obligation with respect to the app. Apple is not responsible for ` +
      `addressing any claims by you or any third party relating to the app, ` +
      `including product-liability claims, claims that the app fails to meet a ` +
      `legal or regulatory requirement, and claims arising under consumer-` +
      `protection or similar law, and Apple is not responsible for investigating, ` +
      `defending, settling, or discharging any third-party claim that the app or ` +
      `your use of it infringes intellectual-property rights. You must use the ` +
      `app in compliance with the App Store Terms of Service, and you represent ` +
      `that you are not located in a country subject to a U.S. Government embargo ` +
      `or designated as a "terrorist-supporting" country, and that you are not on ` +
      `any U.S. Government list of prohibited or restricted parties. Apple and ` +
      `its subsidiaries are third-party beneficiaries of these Terms and may ` +
      `enforce them against you as a user of the iOS app.`,
  },
  {
    heading: 'Contact',
    body:
      `Questions about these Terms? Contact Cordell Jenkins II at ` +
      `cordelljenkins1914@gmail.com.`,
  },
];

const TermsPage = () => (
  <LegalDocumentPage
    title="Terms of Service"
    lastUpdated="July 16, 2026"
    contactEmail="cordelljenkins1914@gmail.com"
    sections={SECTIONS}
    otherDocPath="/privacy"
    otherDocLabel="Privacy Policy →"
  />
);

export default TermsPage;
